// The home notification feed for the panel on Home and the bell in the header
// (TGCLOUD-1896). Both read this one cached list, so they show the same items
// and the same count, and whatever changes it changes both.
//
// It reads again when a clinical record is written (src/axios.js announces
// every write to /clinical-notes), when the window regains focus, and when the
// bell is opened, so closing an appointment takes it off the bell without a
// reload. A dismissal is stored on the server, the reader's own, so it is not
// back on the next read.
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { SnackContext } from 'src/store/ContextStore';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { CLINICAL_RECORDS_CHANGED } from 'src/util/clinicalRecordEvents';
import { feedInWindow, normaliseFeedItems } from 'src/util/notificationFeed';

export const NOTIFICATION_FEED_KEY = 'notificationFeed';

const feedKey = (userId) => [NOTIFICATION_FEED_KEY, String(userId || '')];

/**
 * @param {{ userId: string, enabled?: boolean }} options
 * @returns {{ items: object[]|null, isError: boolean, refetch: function }}
 *   `items` is null until the first answer arrives.
 */
export default function useNotificationFeed({ userId, enabled = true } = {}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: feedKey(userId),
    queryFn: async () =>
      normaliseFeedItems((await axios.get(APICONSTANTS.notificationFeed)).data),
    enabled: enabled && Boolean(userId),
    staleTime: 15_000,
    refetchOnWindowFocus: true
  });

  useEffect(() => {
    const refresh = () =>
      queryClient.invalidateQueries({ queryKey: [NOTIFICATION_FEED_KEY] });
    window.addEventListener(CLINICAL_RECORDS_CHANGED, refresh);
    return () => window.removeEventListener(CLINICAL_RECORDS_CHANGED, refresh);
  }, [queryClient]);

  const items = useMemo(
    () => (query.data ? feedInWindow(query.data) : null),
    [query.data]
  );
  return { items, isError: query.isError, refetch: query.refetch };
}

const sameItem = (a, b) => String(a.id) === String(b.id) && a.type === b.type;

/**
 * Dismiss an item for this reader. It leaves both views at once and the server
 * stores the dismissal, so it stays gone (POST …/notifications/dismissals). If
 * the server refuses, it comes back, with a word as to why.
 *
 * @param {string} userId
 * @returns {(item: { id: string, type: string }) => void}
 */
export function useDismissNotification(userId) {
  const queryClient = useQueryClient();
  const t = useAutoTranslation();
  const { setSnack } = useContext(SnackContext) || {};
  const key = feedKey(userId);

  const { mutate } = useMutation({
    mutationFn: (item) =>
      axios.post(APICONSTANTS.notificationDismissals, {
        noteType: item.type,
        noteId: item.id
      }),
    onMutate: async (item) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData(key);
      queryClient.setQueryData(key, (rows) =>
        rows ? rows.filter((n) => !sameItem(n, item)) : rows
      );
      return { previous };
    },
    onError: (err, item, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
      // A locked workspace is explained once, by the licence banner.
      if (err?.response?.data?.groupAccess) return;
      setSnack?.({
        status: true,
        msg:
          err?.response?.status === 403
            ? t('You cannot dismiss reminders with read-only access.')
            : t('The reminder could not be dismissed. Please try again.'),
        severity: 'error'
      });
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: [NOTIFICATION_FEED_KEY] })
  });

  return useCallback((item) => mutate(item), [mutate]);
}
